/**
 * 待办：报名、办签注、订机票这类**一次性动作**。
 *
 * 和日历上的里程碑刻意分开。里程碑是「那天会发生什么」，看一眼就够；
 * 待办是「你得动手，而且没做完会一直催」—— 报名这种事，提醒弹一次被划掉
 * 就等于没提醒过，所以它必须有完成态，而且完成态要能自己勾。
 *
 * 排序按硬截止，最紧的在最上面；勾掉之后从列表消失，催办同时停止。
 */
import { useCallback, useEffect, useState } from 'react'
import { studyApi, type Task } from '../lib/study'

export default function Tasks() {
  const [list, setList] = useState<Task[]>([])
  const [busy, setBusy] = useState<number | null>(null)

  const load = useCallback(async () => {
    const r = await studyApi.tasks()
    if (r.ok) setList(r.data.open)
  }, [])
  useEffect(() => { void load() }, [load])

  if (!list.length) return null

  const mark = async (t: Task) => {
    setBusy(t.id)
    try { if ((await studyApi.markTask(t.id, true)).ok) await load() } finally { setBusy(null) }
  }

  return (
    <section className="rise rise-2" style={{ display: 'grid', gap: 10 }}>
      <h2 style={{
        margin: '2px 2px -2px', fontSize: 15, fontWeight: 800,
        color: 'var(--ps-text2)', letterSpacing: '.02em',
      }}>要办的事 · {list.length}</h2>
      {list.map((t) => <Row key={t.id} t={t} busy={busy === t.id} onDone={() => mark(t)} />)}
    </section>
  )
}

/** 剩几天决定这一条长什么样：红了就是真的要出事了 */
function heat(t: Task): { bg: string; line: string; fg: string; tag: string } {
  const d = t.daysLeft
  if (d !== null && d < 0) return { bg: 'var(--cal-missed-bg)', line: 'var(--cal-missed-fg)', fg: 'var(--cal-missed-fg)', tag: `已过期 ${-d} 天` }
  if (d !== null && d <= 2) return { bg: 'var(--cal-missed-bg)', line: 'var(--cal-missed-fg)', fg: 'var(--cal-missed-fg)', tag: d === 0 ? '今天最后一天' : `只剩 ${d} 天` }
  if (t.urgent || (d !== null && d <= 7)) return { bg: 'var(--ps-accent-soft)', line: 'var(--ps-accent-line)', fg: 'var(--ps-accent)', tag: d === null ? '要办' : `还有 ${d} 天` }
  return { bg: 'var(--ps-surface)', line: 'var(--ps-line)', fg: 'var(--ps-text2)', tag: d === null ? '要办' : `还有 ${d} 天` }
}

function Row({ t, busy, onDone }: { t: Task; busy: boolean; onDone(): void }) {
  const [open, setOpen] = useState(false)
  const h = heat(t)
  // 说明里带着写库时的内部标记，不该给人看
  const note = (t.note ?? '').replace(/\s*\[[a-z:0-9]+\]/gi, '').trim()

  return (
    <article style={{
      background: h.bg, border: `1px solid ${h.line}`, borderRadius: 18,
      padding: '14px 15px', display: 'grid', gap: 9,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16.5, fontWeight: 800, lineHeight: 1.45, color: 'var(--ps-text)' }}>{t.title}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: h.fg }}>{h.tag}</span>
            {t.due_day && (
              <span className="num" style={{ fontSize: 12.5, color: 'var(--ps-text3)', fontWeight: 700 }}>
                截止 {+t.due_day.slice(5, 7)}/{+t.due_day.slice(8, 10)}
              </span>
            )}
          </div>
        </div>
        <button onClick={() => setOpen((v) => !v)} aria-expanded={open} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px',
          fontSize: 13, fontWeight: 700, color: 'var(--ps-text3)',
        }}>{open ? '收起' : '详情'}</button>
      </div>

      {open && note && (
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.7, color: 'var(--ps-text2)' }}>{note}</p>
      )}

      <div style={{ display: 'flex', gap: 9 }}>
        {t.url && (
          <a href={t.url} target="_blank" rel="noreferrer" style={{
            flex: 1, minHeight: 44, borderRadius: 999, display: 'grid', placeItems: 'center',
            border: `1.5px solid ${h.fg}`, color: h.fg, fontSize: 15, fontWeight: 800, textDecoration: 'none',
          }}>去官网</a>
        )}
        <button onClick={onDone} disabled={busy} style={{
          flex: 1, minHeight: 44, borderRadius: 999, border: 'none', cursor: 'pointer',
          background: 'var(--ps-accent)', color: 'var(--ps-on-accent)', fontSize: 15, fontWeight: 800,
        }}>{busy ? '…' : '办好了'}</button>
      </div>
    </article>
  )
}
